import { rem } from 'polished'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'

// SSR renders up to the desktop count; CSS hides rows beyond the mobile count
// to avoid hydration mismatch from useScreenSizeUp returning false on the server.
const COLLAPSED_COUNT_MOBILE = 3
const COLLAPSED_COUNT_DESKTOP = 5

interface LoungeRow {
  name: string
  amenities: string
}

function parseIncluded(included: string): Array<LoungeRow> {
  return included
    .split('\n')
    .map(line => {
      // Format: - Lounge Name: amenity1, amenity2
      const match = line.match(/^-\s+([^:]+):\s*(.+)$/)
      if (!match) return null
      return { name: match[1].trim(), amenities: match[2].trim() }
    })
    .filter((row): row is LoungeRow => row !== null)
}

const Header = styled(Group)`
  padding-bottom: ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const LoungeRowItem = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(4)};
  padding: ${rem(12)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(200)} 1fr;
    gap: ${rem(16)};
    align-items: start;
  }
`

const RowsWrapper = styled.div<{ $collapsed: boolean }>`
  position: relative;

  ${({ $collapsed }) => $collapsed && css`
    & > *:nth-child(n + ${COLLAPSED_COUNT_MOBILE + 1}) {
      display: none;
    }

    ${mediaQueryUp.tablet} {
      & > *:nth-child(n + ${COLLAPSED_COUNT_MOBILE + 1}) {
        display: grid;
      }
    }
  `}

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    inset-inline: 0;
    height: ${rem(48)};
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
    opacity: ${({ $collapsed }) => ($collapsed ? 1 : 0)};
    pointer-events: none;
    transition: opacity 0.2s;
  }
`

const ToggleLink = styled(TextLink)`
  margin-top: ${rem(8)};
`

interface Props {
  included: string
}

function ExperienceCollinsonLoungeOptions({ included }: Props) {
  const intl = useIntl()
  const [expanded, setExpanded] = useState(false)

  const lounges = parseIncluded(included)
  const showToggle = lounges.length > COLLAPSED_COUNT_DESKTOP
  const visibleLounges = expanded || !showToggle ? lounges : lounges.slice(0, COLLAPSED_COUNT_DESKTOP)

  const headingLabel = intl.formatMessage({
    id: 'Fcp2dozZ6LIDQt21MKfF',
    defaultMessage: 'Lounge options',
    description: 'Section heading for the list of available airport lounges on the Collinson experience offer page',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'qhvEmEklcrYX_1WhqTC1',
    defaultMessage: 'Show more',
    description: 'Button to expand the full list of Collinson lounges on the experience offer page',
  })

  const showLessLabel = intl.formatMessage({
    id: 'tgIyDguaUX-RSirf6lQc',
    defaultMessage: 'Show less',
    description: 'Button to collapse the Collinson lounge list back to the default count on the experience offer page',
  })

  if (lounges.length === 0) return null

  return (
    <VerticalSpacer gap={0}>
      <Header direction="horizontal" gap={8} verticalAlign="center">
        <LineChairWithAirplaneIcon size="S" />
        <BodyText variant="large" weight="bold">{headingLabel}</BodyText>
      </Header>
      <RowsWrapper $collapsed={!expanded && lounges.length > COLLAPSED_COUNT_MOBILE}>
        {visibleLounges.map((lounge, index) => (
          <LoungeRowItem key={`${lounge.name}-${index}`}>
            <BodyText variant="medium" weight="bold">{lounge.name}</BodyText>
            <BodyText variant="medium">{lounge.amenities}</BodyText>
          </LoungeRowItem>
        ))}
      </RowsWrapper>
      {showToggle && (
        <ToggleLink
          size="medium"
          weight="bold"
          endIcon={<DropdownChevron open={expanded} />}
          onClick={() => setExpanded(prev => !prev)}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </ToggleLink>
      )}
    </VerticalSpacer>
  )
}

export default ExperienceCollinsonLoungeOptions
